/**
 * The selected virtual account's panel (TMMAPP-0151 §F 18; prototype P:546–565): the list page's
 * panel under the cards, and the top of `/v/:id`.
 *
 * - On the list page, a heading row: the name (`h2`, 28) with its privacy chip and, at the end, the
 *   served total (`figure`). On `/v/:id` the page header carries those, so the row is left out.
 * - The visibility line: "Visible to you and {names}. … Invited: {names}." on an own shared one,
 *   "Only you can see this virtual account. …" on an own private one, "{owner} owns this virtual
 *   account and shares it with you." on someone else's. Names are served (`sharedWithNames` /
 *   `invitedNames`, TMMAPI-0186 U3).
 * - **Standing agreement:** the served summary `body` as a `brandSoft` callout, when there is one.
 * - The envelope grid the caller renders (the unassigned pool first).
 */
import type { ReactNode } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { currentToolInk } from '@/shell/sidebarPaper';
import { resolveTokens } from '@/theme/theme';

interface VirtualAccountPanelProps {
  /** The list page's heading row (the name, its chip and the total); absent on `/v/:id`. */
  heading?: ReactNode;
  /** The served visibility line; null renders none (the list row has not arrived). */
  visibility: string | null;
  agreement: string | null;
  /** The envelope grid (and anything after it on `/v/:id`). */
  children: ReactNode;
  'aria-label'?: string;
}

export function VirtualAccountPanel({
  heading,
  visibility,
  agreement,
  children,
  'aria-label': ariaLabel,
}: VirtualAccountPanelProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Paper
      component="section"
      data-virtual-account-panel=""
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      sx={{ p: { xs: '18px 16px', sm: '22px' }, display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      {heading}
      {visibility !== null && (
        <Typography
          data-visibility-line=""
          sx={{
            fontSize: 14,
            lineHeight: '20px',
            color: tokens.text2,
            maxWidth: 680,
            mt: heading !== undefined ? '-8px' : 0,
          }}
        >
          {visibility}
        </Typography>
      )}
      {agreement !== null && (
        <Box
          data-standing-agreement=""
          sx={{
            bgcolor: tokens.brandSoft,
            borderRadius: '14px',
            p: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: 12,
              lineHeight: '16px',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              // `brand` on `brandSoft` where it measures 4.5:1, else `text` (the derived current-row ink).
              color: currentToolInk(tokens),
            }}
          >
            {t('virtualAccounts.panel.agreement')}
          </Typography>
          <Typography
            component="span"
            sx={{ fontSize: 14.5, lineHeight: '21px', color: tokens.text, whiteSpace: 'pre-line' }}
          >
            {agreement}
          </Typography>
        </Box>
      )}
      {children}
    </Paper>
  );
}
